import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Home, ShoppingBag, Users, Wallet, User, MessageCircle, CreditCard } from "lucide-react";
import HomePage from "./HomePage";
import WalletPage from "./WalletPage";
import MyTeamPage from "./MyTeamPage";
import ProductPage from "./ProductPage";
import MePage from "./MePage";
import BankCardPage from "./BankCardPage";
import TelegramPopup from "@/components/TelegramPopup";

import { API } from "@/apiConfig";

const tabs = [
  { id: "home", label: "Home", icon: Home },
  { id: "product", label: "Product", icon: ShoppingBag },
  { id: "myteam", label: "Myteam", icon: Users },
  { id: "wallet", label: "Wallet", icon: Wallet },
  { id: "me", label: "Me", icon: User },
];

export default function Dashboard({ user, onLogout, onUserUpdate }) {
  const [activeTab, setActiveTab] = useState("home");
  const [walletSubTab, setWalletSubTab] = useState(null);
  const [showTelegram, setShowTelegram] = useState(true);
  const [profile, setProfile] = useState(user);
  const [appSettings, setAppSettings] = useState({});

  const refreshProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/user/profile`);
      setProfile(data);
      if (onUserUpdate) onUserUpdate(data);
    } catch { }
  };

  const fetchAppSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/app-settings`);
      setAppSettings(data);
    } catch { }
  };

  useEffect(() => {
    refreshProfile();
    fetchAppSettings();
  }, []);

  const handleNavigate = useCallback((tab, subTab) => {
    if (tab === "bankcard") {
      setActiveTab("bankcard");
      return;
    }
    setActiveTab(tab);
    if (subTab) setWalletSubTab(subTab);
    else setWalletSubTab(null);
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case "home":
        return <HomePage profile={profile} onRefresh={refreshProfile} onNavigate={handleNavigate} />;
      case "wallet":
        return <WalletPage profile={profile} onRefresh={refreshProfile} initialSection={walletSubTab} />;
      case "myteam":
        return <MyTeamPage profile={profile} />;
      case "product":
        return <ProductPage profile={profile} onRefresh={refreshProfile} />;
      case "me":
        return <MePage profile={profile} onLogout={onLogout} onRefresh={refreshProfile} />;
      case "bankcard":
        return <BankCardPage onBack={() => setActiveTab("home")} />;
      default:
        return <HomePage profile={profile} onRefresh={refreshProfile} onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="dashboard-container" data-testid="dashboard">
      {showTelegram && appSettings.telegram_link !== "" && (
        <TelegramPopup
          link={appSettings.telegram_link || "https://t.me/amazoneproduct"}
          onClose={() => setShowTelegram(false)}
        />
      )}

      <div className="dashboard-content pb-20">
        {renderTab()}
      </div>

      {/* Help Floating Button */}
      {appSettings.help_link && (
        <a href={appSettings.help_link} target="_blank" rel="noopener noreferrer"
          className="help-float-btn" data-testid="help-float-btn">
          <MessageCircle size={22} />
        </a>
      )}

      {/* Bottom Navigation */}
      <nav className="bottom-nav" data-testid="bottom-nav">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className={`bottom-nav-item ${isActive ? "active" : ""}`}
              onClick={() => { setActiveTab(tab.id); setWalletSubTab(null); }}
              data-testid={`nav-${tab.id}`}
            >
              <Icon size={20} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
